import React, { useEffect, useState } from 'react';
import { hämtaLagervarningar, markeraUtgaende, återställUtgaende, Lagervarningar, LagerVin } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { TEMA, useMobil } from '../theme';

const FÄRG: Record<string, string> = {
  kritisk: '#D9695F',
  lag: TEMA.accent,
  bevaka: '#8A8780',
};

const monoStyle: React.CSSProperties = {
  fontFamily: "'IBM Plex Mono', monospace",
};

const kortStyle: React.CSSProperties = {
  background: TEMA.bgPanel,
  border: `1px solid ${TEMA.border}`,
  borderRadius: '8px',
  padding: '20px 24px',
  marginBottom: '16px',
};

export default function LagtLagersaldo() {
  const [data, setData] = useState<Lagervarningar | null>(null);
  const [laddar, setLaddar] = useState(true);
  const [uppdaterar, setUppdaterar] = useState(false);
  const [fel, setFel] = useState<string | null>(null);
  const [visaUtgående, setVisaUtgående] = useState(false);
  const [arbetarNr, setArbetarNr] = useState<string | null>(null);
  const { profil } = useAuth();
  const ärMobil = useMobil();

  function ladda(uppdatera = false) {
    if (uppdatera) setUppdaterar(true);
    hämtaLagervarningar(uppdatera)
      .then(res => { setData(res.data); setFel(null); })
      .catch(err => {
        console.error('Kunde inte ladda lagervarningar:', err);
        setFel('Kunde inte hämta lagersaldon från Fortnox just nu. Försök igen om en stund.');
      })
      .finally(() => { setLaddar(false); setUppdaterar(false); });
  }

  useEffect(() => { ladda(); }, []);

  async function markera(v: LagerVin) {
    if (!window.confirm(`Markera ${v.namn} som utgående? Det försvinner från varningarna tills saldot ökar igen.`)) return;
    setArbetarNr(v.artikel_nr);
    try {
      await markeraUtgaende({ artikel_nr: v.artikel_nr, namn: v.namn, saldo: v.saldo, markerad_av: profil?.namn || '' });
      // Flytta raden direkt, utan att vänta på att listan hämtas om.
      setData(prev => prev && ({
        ...prev,
        viner: prev.viner.filter(x => x.artikel_nr !== v.artikel_nr),
        antal_kritiska: prev.antal_kritiska - (v.nivå === 'kritisk' ? 1 : 0),
        utgaende: [...prev.utgaende, {
          artikel_nr: v.artikel_nr, plats: v.plats, namn: v.namn, pris: v.pris, saldo: v.saldo,
          markerad_av: profil?.namn || '', markerad: new Date().toISOString(),
        }],
      }));
      ladda();
    } catch (err) {
      console.error('Kunde inte markera som utgående:', err);
      alert('Kunde inte spara. Försök igen.');
    } finally {
      setArbetarNr(null);
    }
  }

  async function återställ(artikelNr: string) {
    setArbetarNr(artikelNr);
    try {
      await återställUtgaende(artikelNr);
      setData(prev => prev && ({ ...prev, utgaende: prev.utgaende.filter(x => x.artikel_nr !== artikelNr) }));
      ladda();
    } catch (err) {
      console.error('Kunde inte återställa:', err);
      alert('Kunde inte spara. Försök igen.');
    } finally {
      setArbetarNr(null);
    }
  }

  if (laddar) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '40vh' }}>
      <p style={{ color: TEMA.inkFaint, fontSize: '14px', fontFamily: "'Inter', sans-serif" }}>Hämtar lagersaldon från Fortnox...</p>
    </div>
  );

  if (fel || !data) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '40vh' }}>
      <p style={{ color: '#D9695F', fontSize: '14px', fontFamily: "'Inter', sans-serif" }}>{fel}</p>
    </div>
  );

  const maxSaldo = Math.max(...data.nivåer.map(n => n.max));
  const grupper = data.nivåer.map(n => ({ ...n, viner: data.viner.filter(v => v.nivå === n.nivå) }));

  return (
    <div>
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ margin: 0, fontFamily: "'Fraunces', serif", fontSize: '30px', color: TEMA.ink }}>Lågt lagersaldo</h1>
        <p style={{ margin: '10px 0 0', fontSize: '13.5px', color: TEMA.inkSoft, fontFamily: "'Inter', sans-serif", maxWidth: '680px', lineHeight: 1.5 }}>
          Viner under {data.prisgräns} kr med disponibelt saldo på {maxSaldo} flaskor eller färre.
          Disponibelt saldo är lagret minus det som redan är sålt men inte levererat.
        </p>
        <p style={{ margin: '8px 0 0', ...monoStyle, fontSize: '12px', color: TEMA.inkFaint }}>
          Från Fortnox · uppdaterat {data.uppdaterad} ·{' '}
          <button
            onClick={() => ladda(true)}
            disabled={uppdaterar}
            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', ...monoStyle, fontSize: '12px', color: TEMA.inkSoft, textDecoration: 'underline' }}
          >
            {uppdaterar ? 'hämtar...' : 'hämta igen'}
          </button>
        </p>
      </div>

      <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
        {grupper.map(g => (
          <div key={g.nivå} style={{
            display: 'flex', alignItems: 'baseline', gap: '8px', padding: '12px 18px', borderRadius: '6px',
            background: TEMA.bgPanel,
            border: `1px solid ${g.nivå === 'kritisk' && g.viner.length > 0 ? 'rgba(217,105,95,0.45)' : TEMA.border}`,
          }}>
            <span style={{ fontFamily: "'Fraunces', serif", fontSize: '26px', color: FÄRG[g.nivå] }}>{g.viner.length}</span>
            <span style={{ fontSize: '13px', color: TEMA.inkSoft, fontFamily: "'Inter', sans-serif" }}>{g.namn.toLowerCase()}</span>
          </div>
        ))}
      </div>

      {data.viner.length === 0 && (
        <div style={{ ...kortStyle, textAlign: 'center', padding: '40px 24px' }}>
          <p style={{ margin: 0, fontSize: '14px', color: TEMA.inkFaint, fontFamily: "'Inter', sans-serif" }}>
            Inga viner under gränserna just nu.
          </p>
        </div>
      )}

      {grupper.filter(g => g.viner.length > 0).map(g => (
        <section key={g.nivå} style={kortStyle}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px', marginBottom: '6px' }}>
            <span style={{ width: '9px', height: '9px', borderRadius: '50%', background: FÄRG[g.nivå], display: 'inline-block' }} />
            <h2 style={{ margin: 0, fontFamily: "'Fraunces', serif", fontWeight: 500, fontSize: '18px', color: TEMA.ink }}>{g.namn}</h2>
            <span style={{ ...monoStyle, fontSize: '12px', color: TEMA.inkFaint }}>{g.min}–{g.max} flaskor · {g.viner.length} st</span>
          </div>
          {g.viner.map((v: LagerVin) => (
            <div key={v.artikel_nr} style={{
              display: 'grid',
              gridTemplateColumns: ärMobil ? 'minmax(0, 1fr) auto' : '64px minmax(0, 1fr) minmax(80px, 200px) 72px',
              gap: ärMobil ? '8px 14px' : '16px', alignItems: 'center', padding: '12px 0', borderTop: `1px solid ${TEMA.border}`,
            }}>
              {!ärMobil && <span style={{ ...monoStyle, fontSize: '11.5px', color: TEMA.inkFaint }}>{v.plats || '—'}</span>}
              <div style={{ minWidth: 0 }}>
                <p style={{ margin: 0, fontSize: '14px', color: TEMA.ink, fontFamily: "'Inter', sans-serif" }}>
                  {ärMobil && v.plats && <span style={{ ...monoStyle, fontSize: '11px', color: TEMA.inkFaint, marginRight: '8px' }}>{v.plats}</span>}
                  {v.namn}
                </p>
                {v.reserverade > 0 && (
                  <p style={{ margin: '3px 0 0', fontSize: '12px', color: TEMA.inkFaint, fontFamily: "'Inter', sans-serif" }}>
                    {v.lager} i lager, varav {v.reserverade} redan sålda
                  </p>
                )}
                <button
                  onClick={() => markera(v)}
                  disabled={arbetarNr === v.artikel_nr}
                  style={{
                    marginTop: '4px', background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                    fontFamily: "'Inter', sans-serif", fontSize: '12px', color: TEMA.inkFaint,
                  }}
                  onMouseEnter={e => { e.currentTarget.style.color = TEMA.inkSoft; }}
                  onMouseLeave={e => { e.currentTarget.style.color = TEMA.inkFaint; }}
                >
                  {arbetarNr === v.artikel_nr ? 'Sparar...' : 'Markera som utgående'}
                </button>
              </div>
              <div style={{
                height: '6px', borderRadius: '3px', background: TEMA.border, overflow: 'hidden',
                ...(ärMobil ? { gridColumn: '1 / -1', gridRow: 2 } : {}),
              }}>
                <div style={{
                  height: '100%', borderRadius: '3px', background: FÄRG[g.nivå],
                  width: `${Math.max(3, Math.round((v.saldo / maxSaldo) * 100))}%`,
                }} />
              </div>
              <div style={{ textAlign: 'right' }}>
                <p style={{ margin: 0, ...monoStyle, fontSize: '15px', fontWeight: 500, color: g.nivå === 'kritisk' ? FÄRG.kritisk : TEMA.ink }}>
                  {v.saldo} fl
                </p>
                <p style={{ margin: '2px 0 0', ...monoStyle, fontSize: '11.5px', color: TEMA.inkFaint }}>{v.pris} kr</p>
              </div>
            </div>
          ))}
        </section>
      ))}

      {data.utgaende.length > 0 && (
        <section style={kortStyle}>
          <button
            onClick={() => setVisaUtgående(v => !v)}
            aria-expanded={visaUtgående}
            style={{
              display: 'flex', alignItems: 'baseline', gap: '12px', width: '100%',
              background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left',
            }}
          >
            <span style={{ width: '9px', height: '9px', borderRadius: '50%', background: TEMA.border, display: 'inline-block' }} />
            <span style={{ fontFamily: "'Fraunces', serif", fontWeight: 500, fontSize: '18px', color: TEMA.inkSoft }}>Utgående</span>
            <span style={{ ...monoStyle, fontSize: '12px', color: TEMA.inkFaint }}>
              {data.utgaende.length} st · visas inte i varningarna
            </span>
            <svg width="10" height="10" viewBox="0 0 8 8" fill="none" style={{
              marginLeft: 'auto', color: TEMA.inkFaint,
              transform: visaUtgående ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s ease',
            }}>
              <path d="M2 1L6 4L2 7" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>

          {visaUtgående && (
            <div style={{ marginTop: '10px' }}>
              <p style={{ margin: '0 0 6px', fontSize: '12.5px', color: TEMA.inkFaint, fontFamily: "'Inter', sans-serif" }}>
                Stiger saldot tas markeringen bort automatiskt, och vinet varnas som vanligt igen.
              </p>
              {data.utgaende.map(u => (
                <div key={u.artikel_nr} style={{
                  display: 'flex', alignItems: 'center', gap: '16px', padding: '10px 0',
                  borderTop: `1px solid ${TEMA.border}`,
                }}>
                  <span style={{ ...monoStyle, fontSize: '11.5px', color: TEMA.inkFaint, width: '64px', flexShrink: 0 }}>{u.plats || '—'}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ margin: 0, fontSize: '13.5px', color: TEMA.inkSoft, fontFamily: "'Inter', sans-serif" }}>{u.namn}</p>
                    <p style={{ margin: '2px 0 0', fontSize: '12px', color: TEMA.inkFaint, fontFamily: "'Inter', sans-serif" }}>
                      {u.saldo} fl kvar
                      {u.markerad_av && ` · markerad av ${u.markerad_av}`}
                      {u.markerad && ` ${new Date(u.markerad).toLocaleDateString('sv-SE', { day: 'numeric', month: 'short' })}`}
                    </p>
                  </div>
                  <button
                    onClick={() => återställ(u.artikel_nr)}
                    disabled={arbetarNr === u.artikel_nr}
                    style={{
                      background: 'transparent', border: `1px solid ${TEMA.border}`, borderRadius: '2px',
                      padding: '6px 12px', cursor: 'pointer', flexShrink: 0,
                      fontFamily: "'Inter', sans-serif", fontSize: '12px', color: TEMA.inkSoft,
                    }}
                  >
                    {arbetarNr === u.artikel_nr ? 'Sparar...' : 'Återställ'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {data.slutsålda > 0 && (
        <p style={{ margin: '4px 0 0', fontSize: '12.5px', color: TEMA.inkFaint, fontFamily: "'Inter', sans-serif" }}>
          {data.slutsålda} viner under {data.prisgräns} kr har 0 i disponibelt saldo och visas inte. De är troligen slutsålda eller utgångna.
        </p>
      )}
    </div>
  );
}
